"use client";

import React, { createContext, useContext, useMemo, useSyncExternalStore } from "react";
import type { LineupPlatform } from "./signature-grid.shared";
import { EMPTY_MARKET_ROWS, type LineupMarketRows } from "./lineup-market.shared";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The page-wide holder for the visitor's locals and RSNs. The page fills it after load; every view
 * and the search read it. A store, so a publish re-renders only the readers.
 */

interface LineupMarketStore {
  get: () => LineupMarketRows;
  subscribe: (listener: () => void) => () => void;
  publish: (rows: LineupMarketRows) => void;
}

function createLineupMarketStore(): LineupMarketStore {
  const listeners = new Set<() => void>();
  let rows = EMPTY_MARKET_ROWS;
  return {
    get: () => rows,
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    publish: next => {
      rows = next;
      listeners.forEach(listener => listener());
    }
  };
}

/** What the page needs to shape a market into rows for THIS lineup. */
export interface LineupMarketTarget {
  platform: LineupPlatform;
  /** The signature columns' codes, in order — RSN ✓ cells are projected onto them. */
  columnCodes: readonly string[];
  publish: (rows: LineupMarketRows) => void;
}

interface LineupMarketContextValue {
  store: LineupMarketStore;
  target: LineupMarketTarget;
}

const LineupMarketContext = createContext<LineupMarketContextValue | null>(null);

const NO_SUBSCRIBE = () => () => undefined;
const EMPTY_GET = () => EMPTY_MARKET_ROWS;

export interface LineupMarketProviderProps {
  platform: LineupPlatform;
  columnCodes: readonly string[];
  children: React.ReactNode;
}

function LineupMarketProviderImpl({ platform, columnCodes, children }: LineupMarketProviderProps) {
  const store = useMemo(createLineupMarketStore, []);
  // Joined so a new array with the same codes keeps the context stable.
  const codesKey = columnCodes.join("|");
  const value = useMemo(
    () => ({
      store,
      target: {
        platform,
        columnCodes: codesKey === "" ? [] : codesKey.split("|"),
        publish: store.publish
      }
    }),
    [store, platform, codesKey]
  );
  return <LineupMarketContext.Provider value={value}>{children}</LineupMarketContext.Provider>;
}
export const LineupMarketProvider = withSafeRender(LineupMarketProviderImpl, "LineupMarketProvider");

/** This market's rows; empty until the page publishes them, and outside a provider. */
export function useLineupMarketRows(): LineupMarketRows {
  const context = useContext(LineupMarketContext);
  return useSyncExternalStore(
    context ? context.store.subscribe : NO_SUBSCRIBE,
    context ? context.store.get : EMPTY_GET,
    EMPTY_GET
  );
}

/** Where the page publishes the market it loaded; `null` outside a lineup. */
export function useLineupMarketTarget(): LineupMarketTarget | null {
  return useContext(LineupMarketContext)?.target ?? null;
}
